import { useState, useMemo } from "react";
import { History, Mail, ChevronDown, ChevronRight, Send, Users, TestTube, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import ScheduledEmailsSection from "./ScheduledEmailsSection";
import MatchDayToggle from "./MatchDayToggle";

interface Request {
  id: string;
  email: string;
  type: string;
  status: string;
  created_at: string;
  credentials_sent_at: string | null;
  followup_sent_at: string | null;
  converted_at: string | null;
  mass_emails_sent: unknown;
  codes_exhausted?: boolean;
  new_codes_email_sent_at?: string | null;
}

interface AdminSidebarProps {
  requests: Request[];
  onRefresh: () => void;
  noTestCodesAvailable?: boolean;
}

const EMAIL_TEMPLATES = {
  urgence_24h: {
    label: "Urgence 24h",
    subject: "24h pour profiter de -20%",
    message: `Votre code : FLASH20

-20% sur tous nos abonnements pendant 24h.

12 mois à 48€ au lieu de 60€.

roidustreaming.fr/nos-offres`,
  },
  comparaison_prix: {
    label: "Comparaison prix",
    subject: "Vos abonnements streaming vous coûtent combien ?",
    message: `La plupart des gens paient entre 50 et 80 euros par mois pour leurs abonnements.

Chez nous, c'est 60 euros pour l'année.

Tout inclus : films, séries, sport en direct.

roidustreaming.fr/nos-offres`,
  },
  derniere_chance: {
    label: "Dernière chance",
    subject: "Dernier message + code -15%",
    message: `On ne vous relancera plus.

Si vous voulez revenir : code RETOUR15 pour -15%.

Valable 48h.

roidustreaming.fr/nos-offres`,
  },
  evenement: {
    label: "Événement",
    subject: "[À personnaliser] Ce week-end en direct",
    message: `[Personnalisez avec les événements du moment]

Tous les matchs en direct, qualité HD.

5€/mois.

roidustreaming.fr/nos-offres`,
  },
  custom: {
    label: "Email personnalisé",
    subject: "",
    message: "",
  },
};

const AdminSidebar = ({ requests, onRefresh, noTestCodesAvailable = false }: AdminSidebarProps) => {
  const [historyOpen, setHistoryOpen] = useState(true);
  const [massEmailOpen, setMassEmailOpen] = useState(false);
  const [codesExhaustedOpen, setCodesExhaustedOpen] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState<string>("urgence_24h");
  const [customSubject, setCustomSubject] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [processingExhausted, setProcessingExhausted] = useState<string | null>(null);
  
  // Spam test states
  const [spamTestOpen, setSpamTestOpen] = useState(false);
  const [spamTestTemplate, setSpamTestTemplate] = useState<string>("urgence_24h");
  const [spamTestSubject, setSpamTestSubject] = useState("");
  const [spamTestMessage, setSpamTestMessage] = useState("");
  const [sendingSpamTest, setSendingSpamTest] = useState(false);
  const TEST_EMAIL = "histoirecoquine843@gmail.com";

  // Get all test requests with credentials sent
  const testsWithCredentials = requests.filter(
    (r) => r.type === "test_24h" && r.credentials_sent_at !== null
  );

  // Get emails that have subscriptions (monthly or yearly)
  const subscribedEmails = useMemo(() => {
    return new Set(
      requests
        .filter((r) => r.type === "sub_monthly" || r.type === "sub_yearly")
        .map((r) => r.email.toLowerCase())
    );
  }, [requests]);

  // Tests with codes exhausted that haven't been processed yet (no email sent)
  const pendingCodesExhausted = useMemo(() => {
    return requests.filter(
      (r) => r.type === "test_24h" && 
             r.codes_exhausted === true && 
             r.credentials_sent_at === null &&
             !r.new_codes_email_sent_at && // Not yet processed
             !subscribedEmails.has(r.email.toLowerCase())
    );
  }, [requests, subscribedEmails]);

  // Tests that didn't convert to subscription (includes tests with credentials sent + processed codes exhausted)
  const unconvertedTests = useMemo(() => {
    // Tests with credentials that didn't convert
    const testsWithCredentialsUnconverted = testsWithCredentials.filter(
      (test) => !subscribedEmails.has(test.email.toLowerCase())
    );
    
    // Tests where codes were exhausted AND already processed (email sent)
    const processedCodesExhausted = requests.filter(
      (r) => r.type === "test_24h" && 
             r.codes_exhausted === true && 
             r.credentials_sent_at === null &&
             r.new_codes_email_sent_at !== null && // Already processed
             !subscribedEmails.has(r.email.toLowerCase())
    );
    
    // Combine both lists, avoiding duplicates by email
    const allEmails = new Set<string>();
    const combined: typeof testsWithCredentialsUnconverted = [];
    
    for (const test of testsWithCredentialsUnconverted) {
      if (!allEmails.has(test.email.toLowerCase())) {
        allEmails.add(test.email.toLowerCase());
        combined.push(test);
      }
    }
    
    for (const test of processedCodesExhausted) {
      if (!allEmails.has(test.email.toLowerCase())) {
        allEmails.add(test.email.toLowerCase());
        combined.push(test);
      }
    }
    
    return combined;
  }, [testsWithCredentials, subscribedEmails, requests]);

  // No longer needed - all tests with codes_exhausted go directly to unconvertedTests
  const toggleEmailSelection = (email: string) => {
    setSelectedEmails((prev) =>
      prev.includes(email)
        ? prev.filter((e) => e !== email)
        : [...prev, email]
    );
  };

  const selectAllUnconverted = () => {
    const emails = unconvertedTests.map((t) => t.email);
    setSelectedEmails(emails);
  };

  const clearSelection = () => {
    setSelectedEmails([]);
  };

  const getCurrentEmailContent = () => {
    if (emailTemplate === "custom") {
      return { subject: customSubject, message: customMessage };
    }
    const template = EMAIL_TEMPLATES[emailTemplate as keyof typeof EMAIL_TEMPLATES];
    return { subject: template.subject, message: template.message };
  };

  const handleSpamTest = async () => {
    let subject = spamTestSubject;
    let message = spamTestMessage;
    
    if (spamTestTemplate !== "custom") {
      const template = EMAIL_TEMPLATES[spamTestTemplate as keyof typeof EMAIL_TEMPLATES];
      subject = template.subject;
      message = template.message;
    }
    
    if (!subject.trim() || !message.trim()) {
      toast.error("Veuillez remplir le sujet et le message");
      return;
    }

    setSendingSpamTest(true);
    try {
      const { error } = await supabase.functions.invoke("send-mass-email", {
        body: { email: TEST_EMAIL, subject, message },
      });

      if (error) throw error;
      toast.success("Email de test envoyé !");
    } catch (error) {
      console.error("Error sending spam test:", error);
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSendingSpamTest(false);
    }
  };

  const handleMassFollowup = async () => {
    if (selectedEmails.length === 0) {
      toast.error("Sélectionnez au moins un email");
      return;
    }

    const { subject, message } = getCurrentEmailContent();
    
    if (!subject.trim() || !message.trim()) {
      toast.error("Veuillez remplir le sujet et le message");
      return;
    }

    setSending(true);
    let successCount = 0;
    let errorCount = 0;

    for (const email of selectedEmails) {
      try {
        const { error } = await supabase.functions.invoke("send-mass-email", {
          body: { email, subject, message },
        });

        if (error) throw error;
        successCount++;
      } catch (error) {
        console.error(`Error sending to ${email}:`, error);
        errorCount++;
      }
    }

    setSending(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} email(s) envoyé(s) avec succès`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} erreur(s) lors de l'envoi`);
    }

    clearSelection();
    onRefresh();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Handle processing a codes exhausted request: send email + mark as processed
  const handleProcessExhausted = async (request: Request) => {
    setProcessingExhausted(request.id);
    try {
      // Send the "no codes available" email
      const { error: emailError } = await supabase.functions.invoke("send-no-codes-available", {
        body: { email: request.email },
      });

      if (emailError) throw emailError;

      // Update the request to mark email as sent
      const { error: updateError } = await supabase
        .from("requests")
        .update({ 
          new_codes_email_sent_at: new Date().toISOString(),
          codes_exhausted: true 
        })
        .eq("id", request.id);

      if (updateError) throw updateError;

      toast.success(`Email envoyé à ${request.email} et transféré vers relance de masse`);
      onRefresh();
    } catch (error) {
      console.error("Error processing exhausted request:", error);
      toast.error("Erreur lors de l'envoi de l'email");
    } finally {
      setProcessingExhausted(null);
    }
  };

  // Handle all pending codes exhausted at once
  const handleProcessAllExhausted = async () => {
    if (pendingCodesExhausted.length === 0) return;
    
    setSending(true);
    let successCount = 0;
    let errorCount = 0;

    for (const request of pendingCodesExhausted) {
      try {
        await supabase.functions.invoke("send-no-codes-available", {
          body: { email: request.email },
        });

        await supabase
          .from("requests")
          .update({ 
            new_codes_email_sent_at: new Date().toISOString(),
            codes_exhausted: true 
          })
          .eq("id", request.id);

        successCount++;
      } catch (error) {
        console.error(`Error processing ${request.email}:`, error);
        errorCount++;
      }
    }

    setSending(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} email(s) envoyé(s) et transférés vers relance de masse`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} erreur(s) lors de l'envoi`);
    }

    onRefresh();
  };

  return (
    <div className="w-[420px] min-w-[420px] bg-card border-r border-border h-full flex flex-col flex-shrink-0">
      <div className="p-4 border-b border-border space-y-3">
        <h2 className="text-lg font-semibold text-foreground">Panel Admin</h2>
        
        {/* Match Day Toggle - Prominent position */}
        <MatchDayToggle />
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-2">
          {/* Historique des Tests */}
          <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between text-foreground hover:bg-muted"
              >
                <div className="flex items-center gap-2">
                  <History className="w-4 h-4 text-primary" />
                  <span>Historique des tests</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    {testsWithCredentials.length}
                  </Badge>
                  {historyOpen ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </div>
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {testsWithCredentials.length === 0 ? (
                  <p className="text-muted-foreground text-sm px-3 py-2">
                    Aucun test envoyé
                  </p>
                ) : (
                  testsWithCredentials.map((test) => {
                    const isConverted = test.converted_at !== null || subscribedEmails.has(test.email.toLowerCase());
                    const massEmails = Array.isArray(test.mass_emails_sent) ? (test.mass_emails_sent as Array<{ date: string; subject: string }>) : [];
                    
                    return (
                      <div
                        key={test.id}
                        className="px-3 py-2 rounded-md bg-muted/50 hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground truncate flex-1">
                            {test.email}
                          </p>
                          {isConverted ? (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1 py-0 border-green-500/50 text-green-400"
                            >
                              ✓ Converti
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1 py-0 border-red-500/50 text-red-400"
                            >
                              Non converti
                            </Badge>
                          )}
                        </div>
                        
                        {/* Timeline des emails */}
                        <div className="mt-2 space-y-1 text-xs">
                          {/* Credentials */}
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                            <span>Identifiants envoyés</span>
                            <span className="ml-auto">{formatDate(test.credentials_sent_at!)}</span>
                          </div>
                          
                          {/* Followup auto */}
                          {test.followup_sent_at && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <div className="w-2 h-2 rounded-full bg-amber-500" />
                              <span>Relance auto (24h)</span>
                              <span className="ml-auto">{formatDate(test.followup_sent_at)}</span>
                            </div>
                          )}
                          
                          {/* Mass emails */}
                          {massEmails.map((email, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-muted-foreground">
                              <div className="w-2 h-2 rounded-full bg-purple-500" />
                              <span className="truncate flex-1" title={email.subject}>
                                {email.subject || "Email de masse"}
                              </span>
                              <span className="ml-auto">{formatDate(email.date)}</span>
                            </div>
                          ))}
                          
                          {/* Conversion */}
                          {test.converted_at && (
                            <div className="flex items-center gap-2 text-green-400">
                              <div className="w-2 h-2 rounded-full bg-green-500" />
                              <span>Converti !</span>
                              <span className="ml-auto">{formatDate(test.converted_at)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Relance de Masse */}
          <Collapsible open={massEmailOpen} onOpenChange={setMassEmailOpen}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between text-foreground hover:bg-muted"
              >
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-secondary" />
                  <span>Relance de masse</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    {unconvertedTests.length} à relancer
                  </Badge>
                  {massEmailOpen ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </div>
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <div className="space-y-4">
                {/* Selection buttons */}
                <div className="flex gap-2 px-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedEmails(unconvertedTests.map((t) => t.email));
                    }}
                    className="flex-1 text-xs border-border"
                  >
                    <Users className="w-3 h-3 mr-1" />
                    Tout ({unconvertedTests.length})
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={clearSelection}
                    className="flex-1 text-xs border-border"
                    disabled={selectedEmails.length === 0}
                  >
                    Annuler
                  </Button>
                </div>

                {/* Email list */}
                <div className="space-y-1 max-h-40 overflow-y-auto px-2">
                  {unconvertedTests.length === 0 ? (
                    <p className="text-muted-foreground text-sm py-2">
                      Aucun email à relancer 🎉
                    </p>
                  ) : (
                    unconvertedTests.map((test) => (
                      <label
                        key={test.id}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted transition-colors cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedEmails.includes(test.email)}
                          onCheckedChange={() => toggleEmailSelection(test.email)}
                        />
                        <span className="text-sm text-foreground truncate flex-1">
                          {test.email}
                        </span>
                        {test.codes_exhausted && !test.credentials_sent_at && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 border-amber-500/50 text-amber-400">
                            Sans test
                          </Badge>
                        )}
                      </label>
                    ))
                  )}
                </div>

                {/* Email composition */}
                {selectedEmails.length > 0 && (
                  <div className="space-y-3 px-2 pt-2 border-t border-border">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Template</Label>
                      <Select value={emailTemplate} onValueChange={setEmailTemplate}>
                        <SelectTrigger className="w-full bg-input border-border text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(EMAIL_TEMPLATES).map(([key, value]) => (
                            <SelectItem key={key} value={key}>
                              {value.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {emailTemplate === "custom" ? (
                      <>
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Sujet</Label>
                          <input
                            type="text"
                            value={customSubject}
                            onChange={(e) => setCustomSubject(e.target.value)}
                            placeholder="Sujet de l'email"
                            className="w-full px-3 py-2 text-sm bg-input border border-border rounded-md text-foreground placeholder:text-muted-foreground"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Message</Label>
                          <Textarea
                            value={customMessage}
                            onChange={(e) => setCustomMessage(e.target.value)}
                            placeholder="Votre message..."
                            className="min-h-[80px] text-sm bg-input border-border"
                          />
                        </div>
                      </>
                    ) : (
                      <div className="bg-muted/50 rounded-md p-3 space-y-2">
                        <p className="text-xs text-muted-foreground">Aperçu :</p>
                        <p className="text-sm font-medium text-foreground">
                          {EMAIL_TEMPLATES[emailTemplate as keyof typeof EMAIL_TEMPLATES]?.subject}
                        </p>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {EMAIL_TEMPLATES[emailTemplate as keyof typeof EMAIL_TEMPLATES]?.message}
                        </p>
                      </div>
                    )}

                    <Button
                      size="sm"
                      onClick={handleMassFollowup}
                      disabled={sending}
                      className="w-full bg-primary hover:bg-primary/90"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {sending
                        ? "Envoi en cours..."
                        : `Envoyer à ${selectedEmails.length} personne(s)`}
                    </Button>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Codes épuisés - Section pour les prospects arrivés quand 0 codes */}
          <Collapsible open={codesExhaustedOpen} onOpenChange={setCodesExhaustedOpen}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between text-foreground hover:bg-muted"
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <span>Codes épuisés</span>
                </div>
                <div className="flex items-center gap-2">
                  {pendingCodesExhausted.length > 0 && (
                    <Badge variant="destructive" className="text-xs animate-pulse">
                      {pendingCodesExhausted.length} en attente
                    </Badge>
                  )}
                  {codesExhaustedOpen ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </div>
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <div className="space-y-3 px-2">
                <p className="text-xs text-muted-foreground">
                  Prospects ayant soumis leur email quand il n'y avait plus de codes test disponibles.
                  Cliquez sur "Traiter" pour envoyer l'email explicatif et les ajouter à la relance de masse.
                </p>
                
                {pendingCodesExhausted.length === 0 ? (
                  <p className="text-muted-foreground text-sm py-2 text-center">
                    ✅ Aucun prospect en attente
                  </p>
                ) : (
                  <>
                    {/* Bouton pour tout traiter */}
                    <Button
                      size="sm"
                      onClick={handleProcessAllExhausted}
                      disabled={sending}
                      className="w-full bg-amber-600 hover:bg-amber-700"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {sending ? "Traitement..." : `Traiter tous (${pendingCodesExhausted.length})`}
                    </Button>
                    
                    {/* Liste des emails en attente */}
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {pendingCodesExhausted.map((request) => (
                        <div
                          key={request.id}
                          className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/50 hover:bg-muted transition-colors"
                        >
                          <span className="text-sm text-foreground truncate flex-1">
                            {request.email}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleProcessExhausted(request)}
                            disabled={processingExhausted === request.id}
                            className="text-xs h-7 px-2 border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                          >
                            {processingExhausted === request.id ? "..." : "Traiter"}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Emails programmés */}
          <ScheduledEmailsSection unconvertedTests={unconvertedTests} onRefresh={onRefresh} />


          {/* Test Spam */}
          <Collapsible open={spamTestOpen} onOpenChange={setSpamTestOpen}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between text-foreground hover:bg-muted"
              >
                <div className="flex items-center gap-2">
                  <TestTube className="w-4 h-4 text-amber-500" />
                  <span>Test Relance Spam</span>
                </div>
                {spamTestOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <div className="space-y-3 px-2">
                <p className="text-xs text-muted-foreground">
                  Envoyer à : histoirecoquine843@gmail.com
                </p>
                
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Template à tester</Label>
                  <Select value={spamTestTemplate} onValueChange={setSpamTestTemplate}>
                    <SelectTrigger className="w-full bg-input border-border text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(EMAIL_TEMPLATES).map(([key, value]) => (
                        <SelectItem key={key} value={key}>
                          {value.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {spamTestTemplate === "custom" ? (
                  <>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Sujet</Label>
                      <input
                        type="text"
                        value={spamTestSubject}
                        onChange={(e) => setSpamTestSubject(e.target.value)}
                        placeholder="Sujet de l'email"
                        className="w-full px-3 py-2 text-sm bg-input border border-border rounded-md text-foreground placeholder:text-muted-foreground"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Message</Label>
                      <Textarea
                        value={spamTestMessage}
                        onChange={(e) => setSpamTestMessage(e.target.value)}
                        placeholder="Votre message..."
                        className="min-h-[80px] text-sm bg-input border-border"
                      />
                    </div>
                  </>
                ) : (
                  <div className="bg-muted/50 rounded-md p-3 space-y-2">
                    <p className="text-xs text-muted-foreground">Aperçu :</p>
                    <p className="text-sm font-medium text-foreground">
                      {EMAIL_TEMPLATES[spamTestTemplate as keyof typeof EMAIL_TEMPLATES]?.subject}
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">
                      {EMAIL_TEMPLATES[spamTestTemplate as keyof typeof EMAIL_TEMPLATES]?.message}
                    </p>
                  </div>
                )}

                <Button
                  size="sm"
                  onClick={handleSpamTest}
                  disabled={sendingSpamTest}
                  className="w-full bg-amber-600 hover:bg-amber-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sendingSpamTest ? "Envoi..." : "Envoyer le test"}
                </Button>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </ScrollArea>

      {/* Stats en bas */}
      <div className="p-4 border-t border-border space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Tests envoyés</span>
          <span className="font-medium text-foreground">
            {testsWithCredentials.length}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Convertis</span>
          <span className="font-medium text-green-400">
            {testsWithCredentials.filter(test => subscribedEmails.has(test.email.toLowerCase()) || test.converted_at !== null).length}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Non convertis</span>
          <span className="font-medium text-red-400">
            {unconvertedTests.length}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AdminSidebar;
